import type { ProjectDetail } from "@/types/api";
import type { Action, MyRole } from "@/types/permissions";

/** What someone is to a project, in one word. See [[permissions.spec]]. */
export type ProjectAccess = "owner" | "edit" | "view" | "contributor";

interface ProjectWithRoles {
  owner: string;
  // Optional so a payload without roles reads as view instead of taking the
  // page down: this is a label, and a label must never be the thing that fails.
  my_roles?: MyRole[];
}

/** The domains whose content a user can add to or change. */
const CONTENT_DOMAINS = ["record", "plan", "tag", "report"];

/** Everything the Contributor role holds, and all it holds. */
const CONTRIBUTOR_ACTIONS: readonly Action[] = [
  "issue:View:Involved",
  "issue:Update:Involved",
];

/** An action that adds or changes content: a non-View verb on a content domain. */
function changesContent(action: Action): boolean {
  const [domain, verb] = action.split(":");
  return CONTENT_DOMAINS.includes(domain) && verb !== "View";
}

/**
 * The level for someone who isn't the owner, from every action their roles
 * hold: `edit` if any adds or changes photos, plans, tags or reports;
 * `contributor` if they hold nothing but the two Involved issue permissions;
 * `view` otherwise.
 */
export function accessForActions(
  actions: readonly Action[],
): Exclude<ProjectAccess, "owner"> {
  if (actions.some(changesContent)) return "edit";
  if (
    actions.length > 0 &&
    actions.every((action) => CONTRIBUTOR_ACTIONS.includes(action))
  ) {
    return "contributor";
  }
  return "view";
}

/**
 * The user's access level to a project: `owner` if it is theirs, else
 * {@link accessForActions} over their roles. A summary for display, never a
 * gate — features check `can()` for the action they need.
 *
 * Roles are read by their permissions, not their names: the names are the
 * backend's and not a contract. Returns `null` until the current user is known,
 * since "owner" cannot be told from "edit" without it.
 */
export function projectAccess(
  project: ProjectWithRoles,
  currentUserId: string | undefined,
): ProjectAccess | null {
  if (!currentUserId) return null;
  if (project.owner === currentUserId) return "owner";

  return accessForActions(
    (project.my_roles ?? []).flatMap((role) =>
      role.permissions.map((permission) => permission.action),
    ),
  );
}

type ProjectWithMembers = Pick<ProjectDetail, "owner" | "members" | "roles">;

/**
 * Every action a member's roles hold, read off the project's own roles: a
 * member lists only their role ids, and the project's `roles` carry each
 * role's actions once.
 *
 * `null` when the person holds no membership — the owner, or someone who has
 * left — or when any of their roles arrived without its actions, as from a
 * backend that predates them. Unknown, never empty: an empty list would read
 * as "can do nothing".
 */
export function memberActions(
  project: ProjectWithMembers,
  userId: string,
): Action[] | null {
  const member = project.members.find((candidate) => candidate.user.id === userId);
  if (!member) return null;

  const rolesById = new Map(project.roles.map((role) => [role.id, role]));
  const actions: Action[] = [];
  for (const { id } of member.roles) {
    const role = rolesById.get(id);
    if (!role?.actions) return null;
    actions.push(...role.actions);
  }
  return actions;
}

/**
 * Someone else's access level on a project, by the same rules as
 * {@link projectAccess}. For the people picker's chips ([[contributor-picker.spec]]).
 * `null` when it can't be told, so no chip shows rather than a wrong one.
 */
export function memberAccess(
  project: ProjectWithMembers,
  userId: string,
): ProjectAccess | null {
  if (project.owner.id === userId) return "owner";
  const actions = memberActions(project, userId);
  return actions ? accessForActions(actions) : null;
}
